import styles from './MapPage.module.scss';

import { DocumentTitle } from '../core/DocumentTitle';
import { VisitDateSelector } from '../visit/VisitDateSelector';
import { VisitStatusSelector } from '../visit/VisitStatusSelector';
import L from 'leaflet';
import React, { useCallback, useEffect } from 'react';
import { MapContainer, Marker, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { MarketSelector } from './MarketSelector';
import { ProviderSelector } from './ProviderSelector';
import { Stack } from '@fluentui/react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/types';
import { setLastMapLocation, setVisitCode } from '../../redux/ui/actions';
import { PageSidePanel } from '../core/PageSidePanel';
import { AsyncVisitSidePanel } from '../core/AsyncVisitSidePanel';
import { getIconDetails } from '../visit/VisitStatusIcon';
import moment from 'moment';
import { ProviderData } from '../../api/providers.types';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { friendlyShortTime } from '../../util/Date';
import { ProviderLocationPlayback } from './ProviderLocationPlayback';
import { AddressInput, SuggestAddress } from '../core/AddressInput';

require('leaflet.awesome-markers/dist/leaflet.awesome-markers');

const doctorIcon = (L as any).AwesomeMarkers.icon({
  icon: 'Location',
  color: 'gray',
  extraClasses: 'ms-Icon',
  prefix: 'ms-Icon-',
});

export const MapPage = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(onPageDidMount('Map'));
    return () => {
      dispatch(onPageWillUnmount('Map'));
    };
  }, [dispatch]);

  return (
    <div className={styles.mapContainer}>
      <DocumentTitle title="Visits Map" />
      <Map />
      <VisitSidePanel />
      <ProviderVisitDetails />
    </div>
  );
};

const Map = () => {
  const visits = useSelector((state: RootState) => state.visits.visits.data || []);
  const marketCodes = useSelector((state: RootState) => state.ui.markets);
  const lastMapLocation = useSelector((state: RootState) => state.ui.lastMapLocation);
  const marketToProviderSchedules = useSelector((state: RootState) => state.providers.marketToProviderSchedules);
  const { date } = useSelector((state: RootState) => state.ui);

  const providerId = useSelector((state: RootState) => state.ui.providerId);
  const location = useSelector((state: RootState) => state.providers.providerLocationHistory[providerId]);

  const dispatch = useDispatch();
  const onClickVisit = useCallback(
    (visitCode: string) => {
      dispatch(setVisitCode(visitCode));
    },
    [dispatch]
  );

  const providersMap: Record<string, ProviderData> = {};
  marketCodes.forEach((m) => {
    const schedules = marketToProviderSchedules[m];
    if (schedules && !schedules.isFetching && schedules.data) {
      schedules.data.map((s) => (providersMap[s.provider.id] = s.provider));
    }
  });
  const providers: ProviderData[] = Object.values(providersMap);

  return (
    <MapContainer
      center={lastMapLocation}
      zoom={11}
      scrollWheelZoom={false}
      whenCreated={(map) => {
        map.addEventListener('moveend', (event) => dispatch(setLastMapLocation(event.target.getCenter())));
      }}
    >
      <TileLayer attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <MapControls defaultDate={date} />

      {visits.map((visit) => {
        const icon = (L as any).AwesomeMarkers.icon({
          icon: getIconDetails(visit.status).icon,
          markerColor: getIconDetails(visit.status).textColor,
          extraClasses: 'ms-Icon',
          prefix: 'ms-Icon-',
        });
        return (
          <Marker
            icon={icon}
            eventHandlers={{ click: () => onClickVisit(visit.visitCode) }}
            position={{ lat: visit.latitudeAddress, lng: visit.longitudeAddress }}
            key={visit.visitCode}
          >
            <Tooltip>
              <div>
                <div>{visit.visitCode}</div>
                <div>{visit.status}</div>
                <div>{visit.doctorName}</div>
                <div>{visit.assistantName}</div>
              </div>
            </Tooltip>
          </Marker>
        );
      })}

      {providers
        .filter((provider) => !providerId || provider.id === providerId)
        .map((provider) => {
          return (
            <Marker icon={doctorIcon} key={provider.id} position={{ lat: provider.latitude, lng: provider.longitude }}>
              <Tooltip>
                <div>
                  <div>{provider.fullName}</div>
                  <div>{provider.availability}</div>
                  <div>{provider.currentVisitCode}</div>
                  {provider.lastPositionTime > 0 && (
                    <div>
                      {moment(provider.lastPositionTime).fromNow()} ({moment(provider.lastPositionTime).format('h:mm a')})
                    </div>
                  )}
                </div>
              </Tooltip>
            </Marker>
          );
        })}

      <ProviderLocationPlayback location={location} />
    </MapContainer>
  );
};

const MapControls = ({ defaultDate }: { defaultDate: string }) => {
  const map = useMap();
  const onAddressSelect = useCallback(
    (address?: SuggestAddress) => {
      if (address) {
        const destination = { lat: address.latitude, lng: address.longitude };
        map.setView(destination);
      }
    },
    [map]
  );

  return (
    <Stack horizontal verticalAlign="start" className={styles.mapControls} tokens={{ childrenGap: 10 }}>
      <ProviderSelector />
      <MarketSelector hideLabel={true} />
      <VisitStatusSelector hideLabel={true} />
      <VisitDateSelector defaultDate={defaultDate} />
      <AddressInput onSelect={onAddressSelect} />
    </Stack>
  );
};

const VisitSidePanel = () => {
  const visitCode = useSelector((state: RootState) => state.ui.visitCode);

  if (!visitCode) {
    return null;
  }

  return (
    <PageSidePanel>
      <AsyncVisitSidePanel visitCode={visitCode} hideMap={true} />
    </PageSidePanel>
  );
};

const ProviderVisitDetails = () => {
  const providerId = useSelector((state: RootState) => state.ui.providerId);
  const visits = useSelector((state: RootState) => state.visits.visits.data || []);
  if (!providerId) {
    return null;
  }

  const filtered = visits.filter((v) => v.doctorId === providerId || v.assistantId === providerId);
  if (filtered.length === 0) {
    return null;
  }

  return (
    <pre className={styles.providerDetails}>
      {'code      start    end\n'}
      {filtered.reverse().map((visit) => {
        let start = friendlyShortTime(new Date(visit.estimatedStartTime));
        while (start.length < 8) {
          start = ' ' + start;
        }
        let end = visit.timeEndedVisit > 0 ? friendlyShortTime(new Date(visit.timeEndedVisit)) : '-';
        while (end.length < 8) {
          end = ' ' + end;
        }

        return `${visit.visitCode} ${start} ${end}\n`;
      })}
    </pre>
  );
};
